// Therapist Session approvals — the Admin half of TSESSION-020/022/023.
//
// ⚠ THIS SCREEN IS LOAD-BEARING, NOT A NICE-TO-HAVE. TSESSION-020 routes a
// below-minimum, weekend-omitting or over-Off-limit proposal to Request for
// Approval; TSESSION-022 requires ADMIN to decide it; TSESSION-023 Auto Declines
// everything unresolved at Thursday 6:00 PM. Without a place to decide, every
// request dies on Thursday and five rules plus a state-machine branch never run.
//
// ⚠ THE PROPOSED DATES ARE READ-ONLY TEXT AND MUST STAY THAT WAY. TSESSION-022:
// "Approval secures exactly the dates stored in the request. Admin must not add
// or replace dates automatically." An editable field here — even one that
// defaults to the stored dates — would hand an approver the power to commit a
// therapist to a week she did not choose. The decide route takes no dates and
// the SQL reads the stored proposal.
//
// Hub Manager can SEE this queue through 0075's RLS and cannot act on it: the
// Edge route is admin+superadmin only. CORE-05A §7.4 — "Visibility does not
// create authority to bypass the approved automated logic."

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import { Badge } from "@bednbody/ui/components/badge";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { Button } from "@bednbody/ui/components/button";
import { PageHeader } from "@bednbody/ui/components/page-header";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import type { ColumnDef } from "@tanstack/react-table";
import { Textarea } from "@bednbody/ui/components/textarea";
import { useState } from "react";
import {
  useDecideTherapistApproval,
  useTherapistApprovalQueue,
  type TherapistApprovalRow,
} from "./therapist-sessions.hooks";
import { CycleStatusCard } from "./CycleStatusCard";
import { TodaySessionsCard } from "./TodaySessionsCard";

const REASON_COPY: Record<string, string> = {
  below_rank_minimum: "Below rank minimum",
  omits_saturday: "Omits Saturday",
  omits_sunday: "Omits Sunday",
  exceeds_off_limit: "Over the area's Off limit",
};

function manilaDate(value: string): string {
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
  }).format(new Date(`${value}T12:00:00+08:00`));
}

/**
 * A moment WITH its date. Weekday and time alone ("Thu 6:00 PM") could not say
 * which Thursday once the queue spans more than one week.
 */
function manilaMoment(iso: string): string {
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: "Asia/Manila",
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(iso));
}

function datesOf(row: TherapistApprovalRow): string {
  return row.selectedDates.map(manilaDate).join(", ") || "None";
}

export function TherapistSessionsPage() {
  const { data, isPending, isError } = useTherapistApprovalQueue();
  const decide = useDecideTherapistApproval();
  const [approving, setApproving] = useState<TherapistApprovalRow | null>(null);
  const [declining, setDeclining] = useState<TherapistApprovalRow | null>(null);
  const [reason, setReason] = useState("");

  const pending = data?.pending ?? [];
  const resolved = data?.decided ?? [];

  // One mutation serves every row, so "busy" is shown on the row it is for.
  const decidingId = decide.isPending ? (decide.variables?.requestId ?? null) : null;

  const openApprove = (row: TherapistApprovalRow) => {
    decide.reset();
    setApproving(row);
  };
  const openDecline = (row: TherapistApprovalRow) => {
    decide.reset();
    setReason("");
    setDeclining(row);
  };
  // A failure stays inside the dialog that caused it; closing clears it.
  const closeDialogs = () => {
    setApproving(null);
    setDeclining(null);
    decide.reset();
  };

  const pendingColumns: ColumnDef<TherapistApprovalRow, unknown>[] = [
    {
      id: "therapist",
      accessorFn: (row) => row.therapistName,
      header: "Therapist",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.therapistName}
          subtitle={
            row.original.weekStartDate
              ? `Week of ${manilaDate(row.original.weekStartDate)}`
              : "Service week unavailable"
          }
          fallback={row.original.therapistName.slice(0, 2).toUpperCase()}
        />
      ),
    },
    {
      id: "week",
      accessorFn: (row) => row.weekStartDate ?? "",
      header: "Service week",
      cell: ({ row }) =>
        row.original.weekStartDate ? manilaDate(row.original.weekStartDate) : "—",
    },
    { id: "dates", accessorFn: datesOf, header: "Proposed days" },
    {
      id: "reasons",
      accessorFn: (row) => row.reasons.join(" "),
      header: "Why",
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.reasons.map((value) => (
            <Badge key={value} variant="muted">
              {REASON_COPY[value] ?? value}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: "deadline",
      accessorFn: (row) => row.decisionDeadlineAt,
      header: "Decide by",
      cell: ({ row }) => manilaMoment(row.original.decisionDeadlineAt),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.therapistName}
          actions={[
            {
              label: decidingId === row.original.id ? "Approving…" : "Approve",
              onSelect: () => openApprove(row.original),
              disabled: decidingId !== null,
            },
            {
              label: decidingId === row.original.id ? "Declining…" : "Decline",
              onSelect: () => openDecline(row.original),
              disabled: decidingId !== null,
            },
          ]}
        />
      ),
    },
  ];
  const resolvedColumns: ColumnDef<TherapistApprovalRow, unknown>[] = [
    {
      id: "therapist",
      accessorFn: (row) => row.therapistName,
      header: "Therapist",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.therapistName}
          subtitle={datesOf(row.original)}
          fallback={row.original.therapistName.slice(0, 2).toUpperCase()}
        />
      ),
    },
    {
      id: "week",
      accessorFn: (row) => row.weekStartDate ?? "",
      header: "Service week",
      cell: ({ row }) =>
        row.original.weekStartDate ? manilaDate(row.original.weekStartDate) : "—",
    },
    {
      id: "state",
      accessorFn: (row) => row.state,
      header: "Outcome",
      cell: ({ row }) => (
        <Badge
          variant={row.original.state === "approved" ? "success" : "muted"}
          className="capitalize"
        >
          {row.original.state.replaceAll("_", " ")}
        </Badge>
      ),
    },
    {
      id: "reason",
      accessorFn: (row) => row.decisionReason ?? "",
      header: "Reason given",
      cell: ({ row }) => row.original.decisionReason ?? "—",
    },
    {
      id: "decided",
      accessorFn: (row) => row.decidedAt ?? "",
      header: "Decided",
      cell: ({ row }) => (
        <>
          {row.original.decidedAt ? manilaMoment(row.original.decidedAt) : "—"}
          {row.original.decidedByName ? (
            <span className="block text-xs text-muted-foreground">
              by {row.original.decidedByName}
            </span>
          ) : null}
        </>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.therapistName}
          actions={[
            {
              label: "Copy request ID",
              onSelect: () => void navigator.clipboard.writeText(row.original.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-8">
      <PageHeader
        title="Therapist sessions"
        description="Daily sessions, session cycle recovery, and weekly approval requests."
      />
      {/* Operations first: this is the live question, and the approval queue
          below is next week's paperwork. The card owns its date, defaulting to
          today in Manila. */}
      <TodaySessionsCard />

      {/* The cycle behind the queue below. Cron-owned; this is the only place
          its state is visible and the only recovery if a run is missed. */}
      <CycleStatusCard />

      <section aria-labelledby="session-approvals-heading" className="flex min-w-0 flex-col gap-4">
        <h2 id="session-approvals-heading" className="type-title-subsection">Session approvals — awaiting a decision</h2>
        {isError ? (
          // ⚠ NOT "Nothing waiting". A failed load that reads as an empty queue
          // is how requests Auto Decline with nobody having seen them.
          <p className="text-sm text-destructive">
            Couldn&rsquo;t load approval requests — check your connection and try again.
          </p>
        ) : (
          <AdminDataTable
            presentation="standalone"
            data={pending}
            columns={pendingColumns}
            getRowId={(row) => row.id}
            searchText={(row) =>
              [row.therapistName, row.weekStartDate, datesOf(row), ...row.reasons].join(" ")
            }
            searchPlaceholder="Search therapist, date, or reason…"
            initialSorting={[{ id: "deadline", desc: false }]}
            loading={isPending}
            emptyTitle="Nothing waiting"
            emptyDescription="Requests appear when a proposal needs an exception."
            exportFilename="therapist-session-approvals"
            exportColumns={[
              { header: "Therapist", value: (row) => row.therapistName },
              { header: "Service week", value: (row) => row.weekStartDate },
              { header: "Proposed days", value: datesOf },
              { header: "Reasons", value: (row) => row.reasons.join("; ") },
              { header: "Deadline", value: (row) => row.decisionDeadlineAt },
            ]}
          />
        )}
      </section>

      <section aria-labelledby="session-decisions-heading" className="flex min-w-0 flex-col gap-4">
        <h2 id="session-decisions-heading" className="type-title-subsection">Decided — newest first</h2>
        {isError ? (
          <p className="text-sm text-destructive">
            Couldn&rsquo;t load decisions — check your connection and try again.
          </p>
        ) : (
          <AdminDataTable
            presentation="standalone"
            data={resolved}
            columns={resolvedColumns}
            getRowId={(row) => row.id}
            searchText={(row) =>
              [
                row.therapistName,
                row.weekStartDate,
                datesOf(row),
                row.state,
                row.decisionReason,
                row.decidedByName,
              ].join(" ")
            }
            searchPlaceholder="Search therapist, outcome, or approver…"
            facets={[
              {
                id: "state",
                label: "Outcome",
                options: [...new Set(resolved.map((row) => row.state))].map((state) => ({
                  value: state,
                  label: state.replaceAll("_", " "),
                })),
                getValue: (row) => row.state,
              },
            ]}
            initialSorting={[{ id: "decided", desc: true }]}
            loading={isPending}
            emptyTitle="No decisions yet"
            exportFilename="therapist-session-decisions"
            exportColumns={[
              { header: "Therapist", value: (row) => row.therapistName },
              { header: "Service week", value: (row) => row.weekStartDate },
              { header: "Proposed days", value: datesOf },
              { header: "Outcome", value: (row) => row.state },
              { header: "Reason", value: (row) => row.decisionReason },
              { header: "Decided at", value: (row) => row.decidedAt },
              { header: "Decided by", value: (row) => row.decidedByName },
            ]}
          />
        )}
      </section>

      {/* Approve commits her proposed dates for the week, so it is never one
          click — the same weight Decline already had. */}
      <AlertDialog
        open={approving !== null}
        onOpenChange={(open) => {
          if (!open && !decide.isPending) closeDialogs();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve this week?</AlertDialogTitle>
            <AlertDialogDescription>
              {approving
                ? `${approving.therapistName} will be secured on ${datesOf(approving)}${
                    approving.weekStartDate
                      ? ` (week of ${manilaDate(approving.weekStartDate)})`
                      : ""
                  }. These are the dates she proposed; they cannot be changed here.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {decide.isError ? (
            <p className="text-sm text-destructive">{decide.error.message}</p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={decide.isPending}>Cancel</AlertDialogCancel>
            <Button
              disabled={decide.isPending}
              onClick={() => {
                if (!approving) return;
                decide.mutate(
                  { requestId: approving.id, decision: "approve" },
                  { onSuccess: closeDialogs },
                );
              }}
            >
              {decide.isPending ? "Approving…" : "Approve"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* A decline requires a reason — 0075 refuses one without it, so the
          control is disabled rather than letting the server say no. */}
      <Dialog
        open={declining !== null}
        onOpenChange={(open) => {
          if (!open && !decide.isPending) closeDialogs();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline this request?</DialogTitle>
            <DialogDescription>
              The therapist sees this reason. She can submit a different week, or take Auto Secure,
              until the deadline.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this week cannot be approved"
            maxLength={500}
          />
          {decide.isError ? (
            <p className="text-sm text-destructive">{decide.error.message}</p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialogs} disabled={decide.isPending}>
              Cancel
            </Button>
            <Button
              disabled={decide.isPending || reason.trim().length === 0}
              onClick={() => {
                if (!declining) return;
                decide.mutate(
                  { requestId: declining.id, decision: "decline", reason: reason.trim() },
                  { onSuccess: closeDialogs },
                );
              }}
            >
              {decide.isPending ? "Declining…" : "Decline request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
